import React, { useEffect, useState, useCallback } from "react";
import { useNotify } from "react-admin";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Drawer,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { activeMethodologies, methodologyStats, bucketTotalsOf } from "../methodologyUtils";
import { isFieldRequired, SEVERITY_COLOR } from "../labels";
import WorkFileSection from "./WorkFileSection";
import AssumptionOptinSelector from "../panels/AssumptionOptinSelector";

// RESUMEN (spec §4): the operative entry point. Answers, in order: ¿cómo está el proyecto? →
// ¿qué necesita atención? → ¿qué hago ahora? → ¿cómo actualizo los datos? Everything derives
// from the shared dataset (completeness + field-level gaps); no new business logic.

const useStyles = makeStyles((theme) => ({
  card: { marginBottom: theme.spacing(2) },
  kicker: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  headerName: { fontSize: 22, fontWeight: 700, lineHeight: 1.1 },
  headerMeta: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 4 },
  stateLabel: { fontSize: 26, fontWeight: 800, lineHeight: 1.1 },
  stateHint: { fontSize: 13, color: theme.palette.text.secondary, marginTop: 4, marginBottom: theme.spacing(2) },
  methGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: theme.spacing(1.5),
  },
  methCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.75),
  },
  methName: { fontSize: 12, color: theme.palette.text.secondary, fontWeight: 600 },
  methState: { fontSize: 16, fontWeight: 800, margin: "4px 0 2px" },
  methPct: { fontSize: 13, fontWeight: 600, color: theme.palette.text.secondary },
  methSub: { fontSize: 12, fontWeight: 600, marginTop: 4 },
  attnGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing(2),
  },
  attnBlock: { textAlign: "left" },
  attnCount: { fontSize: 34, fontWeight: 800, lineHeight: 1 },
  attnTitle: { fontSize: 13, fontWeight: 600, margin: "4px 0 8px", minHeight: 34 },
  recommend: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: 10,
    padding: theme.spacing(2),
  },
  recoText: { fontSize: 14, color: "#1e3a8a", marginBottom: theme.spacing(1.5), maxWidth: "60ch" },
  traceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: theme.spacing(2),
  },
  traceLabel: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: theme.palette.text.secondary },
  traceRow: { display: "flex", justifyContent: "space-between", fontSize: 14, padding: "2px 0" },
  traceVal: { fontWeight: 700 },
  autoRow: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: theme.spacing(2) },
  autoBig: { fontSize: 18, fontWeight: 700 },
  drawer: { width: "min(520px, 92vw)", padding: theme.spacing(3) },
  sectionTitle: { fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: theme.palette.text.secondary },
}));

const pct = (v) => (v === null || v === undefined ? "—" : Math.round(v) + "%");

const ResumenScreen = ({ api, projectId, projectName, contracted, data, onNavigate, tabs }) => {
  const classes = useStyles();
  const { summary, articles, gapFields, loading, error, reload } = data;

  const notify = useNotify();
  const [optin, setOptin] = useState({ enabled: 0, total: 0 });
  const [configOpen, setConfigOpen] = useState(false);
  const [recalcing, setRecalcing] = useState(false);

  // Recalcular estado: re-run the completeness job WITHOUT re-uploading data (e.g. after a
  // field-definition classification change). Sits next to the project title.
  const recalc = async () => {
    setRecalcing(true);
    const r = await api("POST", `/projects/${projectId}/triggerCompleteness`);
    setRecalcing(false);
    notify(
      r.ok ? "Recálculo lanzado — el estado se actualizará en unos segundos" : `Error ${r.status} al recalcular`,
      r.ok ? "info" : "error"
    );
    if (r.ok && reload) {
      reload();
      setTimeout(reload, 5000);
    }
  };

  const loadOptin = useCallback(async () => {
    if (!projectId) return;
    const r = await api("GET", `/projects/${projectId}/assumption-optin`);
    const fields = r.ok ? r.data.fields ?? [] : [];
    setOptin({ enabled: fields.filter((f) => f.enabled).length, total: fields.length });
  }, [api, projectId]);

  useEffect(() => {
    loadOptin();
  }, [loadOptin]);

  const methodologies = activeMethodologies(articles, contracted);

  // Bucket rollup (spec §4.3 / §4.7). Only fields REQUIRED (M/M-A) for a contracted methodology
  // count as "atención"; Recommended-only (R) fields are optional and tallied apart so they do
  // not inflate the headline (this is the fix for the "abruma" — see review).
  // Every counter here is in "valores" (gap instances), stated explicitly in the UI.
  const totals = { faltan: 0, revisar: 0, asumibles: 0 };
  let optionalPending = 0;
  (gapFields || []).forEach((f) => {
    const b = bucketTotalsOf(f.statusCounts);
    const pend = b.faltan + b.revisar + b.asumibles;
    if (isFieldRequired(f, contracted)) {
      totals.faltan += b.faltan;
      totals.revisar += b.revisar;
      totals.asumibles += b.asumibles;
    } else {
      optionalPending += pend;
    }
  });
  const totalPendientes = totals.faltan + totals.revisar + totals.asumibles;

  // Pending "valores" (gap instances) for a methodology = unsatisfied gaps on fields required
  // (M/M-A) for it. Used per-methodology and to name the project's blocker.
  const methodologyPendingValues = (m) =>
    (gapFields || []).reduce((n, f) => {
      if (!isFieldRequired(f, [m])) return n;
      const b = bucketTotalsOf(f.statusCounts);
      return n + b.faltan + b.revisar + b.asumibles;
    }, 0);

  // Explicit per-methodology state (spec input 4: "LCA · Listo · 100%"). Severity ranks the
  // worst methodology, which drives the single derived project sentence below (input 4b).
  const methInfo = methodologies.map((m) => {
    const s = methodologyStats(articles, m);
    const st =
      s.blocked > 0
        ? { word: "Bloqueado", sev: 3, color: SEVERITY_COLOR.critical }
        : s.incomplete > 0
        ? { word: "Incompleto", sev: 2, color: SEVERITY_COLOR.warn }
        : s.withAssum > 0
        ? { word: "Listo · con asumidos", sev: 1, color: SEVERITY_COLOR.ok }
        : { word: "Listo", sev: 0, color: SEVERITY_COLOR.ok };
    return { m, avg: s.avg, pending: methodologyPendingValues(m), state: st };
  });
  const worst = methInfo.slice().sort((a, b) => b.state.sev - a.state.sev)[0] || null;

  // Factual project state — no optimistic interpretation ("Casi listo"). The label is a plain
  // state; the hint just enumerates each methodology in plain words (input: the system should not
  // interpret for the user).
  const projectHeadline = !worst
    ? { label: "Sin datos todavía", color: SEVERITY_COLOR.neutral }
    : worst.state.sev === 0 || worst.state.sev === 1
    ? { label: "Listo para calcular", color: SEVERITY_COLOR.ok }
    : worst.state.sev === 3
    ? { label: "Bloqueado", color: SEVERITY_COLOR.critical }
    : { label: "Necesita atención", color: SEVERITY_COLOR.warn };
  const projectHint = methInfo
    .map((mi) =>
      mi.state.sev === 3
        ? `${mi.m} está bloqueado`
        : mi.state.sev === 2
        ? `${mi.m} tiene datos pendientes`
        : `${mi.m} está listo`
    )
    .join(". ") + (methInfo.length ? "." : "");

  const lastUpdate = (articles || [])
    .map((a) => a.lastCompletenessCheck)
    .filter(Boolean)
    .sort()
    .pop();

  const goPendientes = (filter) => onNavigate && onNavigate(tabs.PENDIENTES, filter);

  // Loading / empty states (§9).
  if (loading && !articles.length && !gapFields.length) {
    return (
      <Card className={classes.card}>
        <CardContent>
          <Box textAlign="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* ══ 4.1 Cabecera ══ */}
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.kicker}>Data Seamless</div>
          <Box display="flex" alignItems="center" style={{ gap: 12, flexWrap: "wrap" }}>
            <div className={classes.headerName}>{projectName}</div>
            <Button variant="outlined" size="small" onClick={recalc} disabled={recalcing}>
              {recalcing ? <CircularProgress size={18} /> : "Recalcular estado"}
            </Button>
          </Box>
          <div className={classes.headerMeta}>
            {methodologies.length ? methodologies.join(" · ") : "Sin metodologías activas"}
            {" · "}
            Última actualización: {lastUpdate ? new Date(lastUpdate).toLocaleString() : "—"}
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className={classes.card}>
          <CardContent>
            <Typography color="error">{error}</Typography>
            <Button size="small" onClick={reload} style={{ marginTop: 8 }}>
              Reintentar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ══ 4.2 Estado del proyecto ══ */}
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.kicker}>Estado del proyecto</div>
          {/* Single derived headline that names the blocker — no abstract global state competing
              with the per-methodology cards (input 4b). */}
          <div className={classes.stateLabel} style={{ color: projectHeadline.color }}>
            {projectHeadline.label}
          </div>
          <div className={classes.stateHint}>{projectHint}</div>
          {methInfo.length > 0 && (
            <div className={classes.methGrid}>
              {methInfo.map((mi) => (
                <div key={mi.m} className={classes.methCard}>
                  <div className={classes.methName}>{mi.m}</div>
                  {/* Decision (state) weighs more than the metric (%) — state prominent, % secondary. */}
                  <div className={classes.methState} style={{ color: mi.state.color }}>
                    {mi.state.sev <= 1 ? "✓ " : ""}
                    {mi.state.word}
                  </div>
                  <div className={classes.methPct}>{mi.avg == null ? "—" : mi.avg + "% completo"}</div>
                  <div className={classes.methSub} style={{ color: mi.pending ? mi.state.color : "#94a3b8" }}>
                    {mi.pending
                      ? `${mi.pending} valor${mi.pending === 1 ? "" : "es"} ${mi.pending === 1 ? "impide" : "impiden"} completarlo`
                      : "Sin pendientes obligatorios"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ══ 4.3 Qué necesita atención ══ */}
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.kicker}>Qué necesita atención</div>
          {/* Partición explícita: total = requieren atención (obligatorio) + recomendados (opcional).
              Los conjuntos son DISJUNTOS — se muestran los tres para que la relación sea inequívoca. */}
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
            <strong>{totalPendientes + optionalPending}</strong> valor{totalPendientes + optionalPending === 1 ? "" : "es"} sin completar
            {" · "}
            <strong style={{ color: "#d97706" }}>{totalPendientes}</strong> requiere{totalPendientes === 1 ? "" : "n"} atención
            {optionalPending > 0 && (
              <>
                {" · "}
                <strong style={{ color: "#94a3b8" }}>{optionalPending}</strong> recomendado{optionalPending === 1 ? "" : "s"}
              </>
            )}
          </Typography>

          {totalPendientes === 0 ? (
            <Typography style={{ color: "#059669", marginTop: 8 }}>Nada obligatorio pendiente.</Typography>
          ) : (
            // Breakdown of "requieren atención" — los tres suman exactamente esa cifra.
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap", marginTop: 10 }}>
              <CountItem n={totals.asumibles} label="Puedes asumir" color="#d97706" onClick={() => goPendientes("asumibles")} />
              <CountItem n={totals.faltan} label="Faltan datos" color="#dc2626" onClick={() => goPendientes("faltan")} />
              <CountItem n={totals.revisar} label="Datos inválidos" color="#9333ea" onClick={() => goPendientes("invalidos")} />
            </div>
          )}

          {/* Recomendados: opcionales, NO cuentan en "requieren atención" (ya sumados aparte arriba). */}
          {optionalPending > 0 && (
            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: "1px dashed #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 13, color: "#64748b" }}>
                Los {optionalPending} recomendados no bloquean las metodologías actuales.
              </div>
              <Button size="small" color="primary" onClick={() => goPendientes(null)}>
                Ver recomendados →
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ══ 4.4 Siguiente acción recomendada ══ */}
      {totals.asumibles > 0 && (
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.kicker}>Recomendado</div>
            <div className={classes.recommend}>
              <div className={classes.recoText}>
                Puedes resolver {totals.asumibles} pendiente{totals.asumibles === 1 ? "" : "s"} ahora
                utilizando valores disponibles en Bcome.
              </div>
              <Button variant="contained" color="primary" onClick={() => goPendientes("asumibles")}>
                Revisar los {totals.asumibles} asumibles
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ══ 4.5 / 4.6 Archivo de trabajo ══ */}
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.kicker}>Archivo de trabajo</div>
          <WorkFileSection
            api={api}
            projectId={projectId}
            projectName={projectName}
            lastUpdate={lastUpdate}
            onReload={reload}
          />
        </CardContent>
      </Card>

      {/* ══ 4.7 Trazabilidad y origen ══ */}
      <Card className={classes.card}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="baseline">
            <div className={classes.kicker}>Trazabilidad y origen de los datos</div>
            <Button size="small" onClick={() => onNavigate && onNavigate(tabs.DATOS)}>
              Ver datos →
            </Button>
          </Box>
          <div className={classes.traceGrid}>
            <div>
              <div className={classes.traceLabel}>Trazabilidad (clic para ver qué falta)</div>
              <button
                type="button"
                className={classes.traceRow}
                style={{ width: "100%", border: "none", background: "none", font: "inherit", cursor: "pointer", padding: "2px 0" }}
                onClick={() => onNavigate && onNavigate(tabs.DATOS, { level: "L1" })}
              >
                <span>L1 →</span>
                <span className={classes.traceVal}>{pct(summary.avgL1)}</span>
              </button>
              <button
                type="button"
                className={classes.traceRow}
                style={{ width: "100%", border: "none", background: "none", font: "inherit", cursor: "pointer", padding: "2px 0" }}
                onClick={() => onNavigate && onNavigate(tabs.DATOS, { level: "L2" })}
              >
                <span>L2 →</span>
                <span className={classes.traceVal}>{pct(summary.avgL2)}</span>
              </button>
            </div>
            <div>
              <div className={classes.traceLabel}>Origen del dato</div>
              {/* Dos métricas de universos DISTINTOS — se nombran con su denominador para que no
                  parezcan complementarias (avgBrandData = % del total; avgAssumptionRate = % de lo resuelto). */}
              <div className={classes.traceRow}>
                <span>
                  Dato real de marca
                  <span style={{ display: "block", fontSize: 11, color: "#94a3b8" }}>del total de campos</span>
                </span>
                <span className={classes.traceVal}>{pct(summary.avgBrandData)}</span>
              </div>
              <div className={classes.traceRow}>
                <span>
                  Asumido por Bcome
                  <span style={{ display: "block", fontSize: 11, color: "#94a3b8" }}>de los datos ya resueltos</span>
                </span>
                <span className={classes.traceVal}>{pct(summary.avgAssumptionRate)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ══ 4.8 Automatización de assumptions ══ */}
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.kicker}>Automatización</div>
          <div className={classes.autoRow}>
            <div>
              <div className={classes.autoBig}>Valores asumidos automáticamente</div>
              <Typography variant="body2" color="textSecondary">
                {optin.total ? `${optin.enabled} de ${optin.total} campos autorizados` : "—"}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ maxWidth: "56ch", marginTop: 6 }}>
                Permites que Bcome complete automáticamente determinados campos cuando falte
                información.
              </Typography>
            </div>
            <Button variant="outlined" onClick={() => setConfigOpen(true)}>
              Configurar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Drawer anchor="right" open={configOpen} onClose={() => setConfigOpen(false)}>
        <div className={classes.drawer}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <span className={classes.sectionTitle}>Automatización de assumptions</span>
            <Button size="small" onClick={() => setConfigOpen(false)}>
              Cerrar
            </Button>
          </Box>
          <Divider style={{ marginBottom: 16 }} />
          <AssumptionOptinSelector api={api} projectId={projectId} />
        </div>
      </Drawer>
    </>
  );
};

// Compact count for §4.3 — big number + category, clickable. The three together sum to the
// "requieren atención" figure; recomendados live in a separate block, never mixed in.
const CountItem = ({ n, label, color, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!n}
    style={{
      border: "none",
      background: "none",
      padding: 0,
      textAlign: "left",
      cursor: n ? "pointer" : "default",
      font: "inherit",
    }}
  >
    <span>
      <span style={{ fontSize: 30, fontWeight: 800, color: n ? color : "#cbd5e1", lineHeight: 1 }}>{n}</span>
      <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: 4 }}>valor{n === 1 ? "" : "es"}</span>
    </span>
    <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: n ? "#334155" : "#94a3b8", marginTop: 2 }}>
      {label}
    </span>
  </button>
);

export default ResumenScreen;
